import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import type { Friend } from '../types/social';

interface FriendStore {
  friends: Friend[];
  pendingRequests: Friend[];
  sentRequests: Friend[];
  loading: boolean;
  error: string | null;
  fetchFriends: () => Promise<void>;
  fetchPendingRequests: () => Promise<void>;
  fetchSentRequests: () => Promise<void>;
  addFriend: (email: string) => Promise<void>;
  acceptFriend: (friendId: string) => Promise<void>;
  rejectFriend: (friendId: string) => Promise<void>;
}

export const useFriendStore = create<FriendStore>((set, get) => ({
  friends: [],
  pendingRequests: [],
  sentRequests: [],
  loading: false,
  error: null,

  fetchFriends: async () => {
    set({ loading: true, error: null });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        set({ friends: [] });
        return;
      }

      const { data, error } = await supabase
        .from('friendships')
        .select(`
          id,
          friend:user_profiles!friendships_friend_id_fkey (
            id,
            email,
            display_name,
            avatar_url
          ),
          created_at,
          status
        `)
        .eq('user_id', user.id)
        .eq('status', 'accepted');

      if (error) throw error;

      set({
        friends: data?.map(f => ({
          id: f.friend?.id,
          email: f.friend?.email,
          created_at: f.created_at,
          status: 'accepted'
        })) || []
      });
    } catch (err) {
      console.error('Error fetching friends:', err);
      set({ error: err instanceof Error ? err.message : 'Failed to fetch friends' });
    } finally {
      set({ loading: false });
    }
  },

  fetchPendingRequests: async () => {
    set({ loading: true, error: null });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        set({ pendingRequests: [] });
        return;
      }

      const { data, error } = await supabase
        .from('friendships')
        .select(`
          id,
          user:user_profiles!friendships_user_id_fkey (
            id,
            email,
            display_name,
            avatar_url
          ),
          created_at,
          status
        `)
        .eq('friend_id', user.id)
        .eq('status', 'pending');

      if (error) throw error;

      set({
        pendingRequests: data?.map(p => ({
          id: p.user?.id,
          email: p.user?.email,
          created_at: p.created_at,
          status: 'pending'
        })) || []
      });
    } catch (err) {
      console.error('Error fetching pending requests:', err);
      set({ error: err instanceof Error ? err.message : 'Failed to fetch pending requests' });
    } finally {
      set({ loading: false });
    }
  },

  fetchSentRequests: async () => {
    set({ loading: true, error: null });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        set({ sentRequests: [] });
        return;
      }

      const { data, error } = await supabase
        .from('friendships')
        .select(`
          id,
          friend:user_profiles!friendships_friend_id_fkey (
            id,
            email,
            display_name,
            avatar_url
          ),
          created_at,
          status
        `)
        .eq('user_id', user.id)
        .eq('status', 'pending');

      if (error) throw error;

      set({
        sentRequests: data?.map(s => ({
          id: s.friend?.id,
          email: s.friend?.email,
          created_at: s.created_at,
          status: 'pending'
        })) || []
      });
    } catch (err) {
      console.error('Error fetching sent requests:', err);
      set({ error: err instanceof Error ? err.message : 'Failed to fetch sent requests' });
    } finally {
      set({ loading: false });
    }
  },

  addFriend: async (email: string) => {
    set({ loading: true, error: null });
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const { error } = await supabase
        .rpc('handle_friend_request', { friend_email: email }, {
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json'
          }
        });

      console.log(error)
      if (error) throw error;
      await get().fetchSentRequests();
    } catch (err) {
      console.error('Error adding friend:', err);
      set({ error: err instanceof Error ? err.message : 'Failed to add friend' });
    } finally {
      set({ loading: false });
    }
  },

  acceptFriend: async (friendId: string) => {
    set({ loading: true, error: null });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('friendships')
        .update({ status: 'accepted' })
        .eq('user_id', friendId)
        .eq('friend_id', user.id);

      if (error) throw error;
      
      // Refresh both friends and pending requests
      await Promise.all([
        get().fetchFriends(),
        get().fetchPendingRequests()
      ]);
    } catch (err) {
      console.error('Error accepting friend:', err);
      set({ error: err instanceof Error ? err.message : 'Failed to accept friend' });
    } finally {
      set({ loading: false });
    }
  },

  rejectFriend: async (friendId: string) => {
    set({ loading: true, error: null });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('friendships')
        .delete()
        .eq('user_id', friendId)
        .eq('friend_id', user.id);

      if (error) throw error;
      await get().fetchPendingRequests();
    } catch (err) {
      console.error('Error rejecting friend:', err);
      set({ error: err instanceof Error ? err.message : 'Failed to reject friend' });
    } finally {
      set({ loading: false });
    }
  }
}));